'use client'

import {
  type ChangeEventHandler,
  type FC,
  useEffect,
  useRef,
  useState,
} from 'react'
import { FormattedMessage } from 'react-intl'

import { FormControl, FormHelperText } from 'base-ui'

import { AfterSalesImageControlAddImageButton } from './AfterSalesImageControlAddImageButton'
import { type AfterSalesImageControlImageThumbnailImage } from './AfterSalesImageControlImageThumbnail'
import { AfterSalesImageControlImageThumbnailList } from './AfterSalesImageControlImageThumbnailList'
import { AfterSalesImageControlMaxImagesNotification } from './AfterSalesImageControlMaxImagesNotification'

type AfterSalesImageControlProps = {
  errorImageUpload?: string
  isMandatory?: boolean
  maxImages: number
  onChangeImageUpload: (files: File[]) => void
  showMaxImagesNotification?: boolean
}

export const AfterSalesImageControl: FC<AfterSalesImageControlProps> = ({
  errorImageUpload,
  isMandatory,
  maxImages,
  onChangeImageUpload,
  showMaxImagesNotification,
}) => {
  const [images, setImages] = useState<
    AfterSalesImageControlImageThumbnailImage[]
  >([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imagesRef = useRef<AfterSalesImageControlImageThumbnailImage[]>([])
  const hasImages = images.length > 0
  const canAddMoreImages = images.length < maxImages
  const remainingSlots = Math.max(0, maxImages - images.length)

  useEffect((): (() => void) => {
    return (): void => {
      for (const image of imagesRef.current) {
        URL.revokeObjectURL(image.previewUrl)
      }
    }
  }, [])
  const handleImageSelect: ChangeEventHandler<HTMLInputElement> = (
    event,
  ): void => {
    const selectedFiles = [...(event.target.files ?? [])]

    if (remainingSlots === 0 || selectedFiles.length === 0) {
      return
    }

    const filesToAdd = selectedFiles.slice(0, remainingSlots)
    const newItems: AfterSalesImageControlImageThumbnailImage[] =
      filesToAdd.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }))
    setImages((prevImages) => {
      const nextImages = [...prevImages, ...newItems]
      imagesRef.current = nextImages
      onChangeImageUpload(nextImages.map((item) => item.file))

      return nextImages
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  const handleRemoveImage = (index: number): void => {
    setImages((prevImages) => {
      const imageToRemove = prevImages[index]

      URL.revokeObjectURL(imageToRemove.previewUrl)

      const nextImages = prevImages.filter(
        (item, itemIndex) => itemIndex !== index,
      )
      imagesRef.current = nextImages
      onChangeImageUpload(nextImages.map((item) => item.file))

      return nextImages
    })
  }

  const handleOpenFileDialog = (): void => {
    fileInputRef.current?.click()
  }

  return (
    <div className="pt-2">
      <FormControl className="mt-2" error={Boolean(errorImageUpload)}>
        <input
          accept=".jpg, .jpeg, .png"
          data-qa-id="after-sales-image-control-image-upload"
          hidden
          multiple={maxImages > 1}
          onChange={handleImageSelect}
          ref={fileInputRef}
          type="file"
        />

        <div className="flex flex-row items-center font-medium no-underline">
          {showMaxImagesNotification || canAddMoreImages ? (
            <AfterSalesImageControlAddImageButton
              canAddMoreImages={canAddMoreImages}
              isMandatory={isMandatory}
              onClick={handleOpenFileDialog}
            />
          ) : null}
        </div>
        {!canAddMoreImages && showMaxImagesNotification ? (
          <AfterSalesImageControlMaxImagesNotification maxImages={maxImages} />
        ) : null}
        <span className="my-2 block text-m text-dark-primary-strong">
          <FormattedMessage id="damagedProducts.label.allowedImagedFormatAndSize" />
        </span>
        {hasImages ? (
          <AfterSalesImageControlImageThumbnailList
            images={images}
            onRemoveImage={handleRemoveImage}
          />
        ) : null}
        {errorImageUpload ? (
          <FormHelperText>{errorImageUpload}</FormHelperText>
        ) : null}
      </FormControl>
    </div>
  )
}
